/**
 * Itinerary Export Service
 *
 * Handles Excel and CSV export of trip itinerary data
 * Integrates with actual data services to gather trip information
 */

import excelGenerator from '../formats/excel';
import csvGenerator from '../formats/csv';

class ItineraryExportService {
  constructor() {
    this.supportedFormats = ['xlsx', 'csv'];
    this.notifications = {
      started: null,
      completed: null,
      error: null
    };
  }

  /**
   * Export itinerary in specified format
   *
   * @param {Object} context - Current application context
   * @param {string} format - Export format ('xlsx' or 'csv')
   * @returns {Promise<boolean>} Success status
   */
  async exportItinerary(context, format = 'xlsx') {
    try {
      console.log('[EXPORT DEBUG] Format selected:', format);

      if (!this.supportedFormats.includes(format)) {
        throw new Error(`Unsupported format: ${format}`);
      }

      this.showNotification('started', format);

      // Get structured itinerary data
      console.log('[EXPORT DEBUG] Gathering itinerary data...');
      const itineraryData = await this.gatherItineraryData(context);
      console.log('[EXPORT DEBUG] Itinerary data gathered:', {
        dataCount: itineraryData?.length,
        firstItem: itineraryData?.[0]
      });

      if (!itineraryData || itineraryData.length === 0) {
        throw new Error('No itinerary data found for this trip');
      }

      // Generate and download file
      if (format === 'xlsx') {
        console.log('[EXPORT DEBUG] Calling Excel generator');
        await this.generateExcelExport(itineraryData, context.trip);
      } else if (format === 'csv') {
        console.log('[EXPORT DEBUG] Calling CSV generator');
        await this.generateCsvExport(itineraryData, context.trip);
      }

      this.showNotification('completed', format);
      return true;

    } catch (error) {
      console.error('[EXPORT ERROR] Itinerary export failed:', error);
      this.showNotification('error', error.message);
      return false;
    }
  }

  /**
   * Get trip summary for display
   *
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Trip summary
   */
  async getTripSummary(context) {
    try {
      // In a real implementation, this would fetch from actual services
      // For now, use context data and add some mock enhancements

      const trip = context.trip || {};
      const days = await this.getDaysCount(context);

      return {
        name: trip.name || 'My Trip',
        destination: trip.destination || 'Unknown Destination',
        days: days,
        travelers: trip.travelers || 1,
        dateRange: this.formatDateRange(trip.startDate, trip.endDate),
        startDate: trip.startDate,
        endDate: trip.endDate
      };

    } catch (error) {
      console.error('Failed to get trip summary:', error);
      return null;
    }
  }

  /**
   * Get export size estimate
   *
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Export estimate
   */
  async getExportEstimate(context) {
    try {
      const daysCount = await this.getDaysCount(context);
      const activitiesCount = await this.getActivitiesCount(context);

      return {
        daysCount,
        activitiesCount,
        estimatedSize: this.calculateEstimatedSize(daysCount, activitiesCount)
      };

    } catch (error) {
      console.error('Failed to generate export estimate:', error);
      return {
        daysCount: 0,
        activitiesCount: 0,
        estimatedSize: '< 1KB'
      };
    }
  }

  /**
   * Gather structured itinerary data from all sources
   *
   * @param {Object} context - Application context
   * @returns {Promise<Array>} Structured itinerary data
   */
  async gatherItineraryData(context) {
    try {
      // In a real implementation, this would integrate with actual services:
      // const daysService = require('@trip-planner-monorepo/days').daysService;
      // const accommodationService = require('@trip-planner-monorepo/accommodation').accommodationService;
      // const travelService = require('@trip-planner-monorepo/travel').travelService;
      // const placesService = require('@trip-planner-monorepo/places').placesService;

      // For now, return structured mock data that represents real itinerary structure
      return this.generateMockItineraryData(context);

    } catch (error) {
      console.error('Failed to gather itinerary data:', error);
      throw error;
    }
  }

  /**
   * Generate mock itinerary data (to be replaced with real service integration)
   */
  generateMockItineraryData(context) {
    const trip = context.trip || {};
    const startDate = new Date(trip.startDate || '2025-03-15');
    const days = [];

    // Generate sample data for demonstration
    for (let i = 0; i < 5; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);

      days.push({
        dayNumber: i + 1,
        date: currentDate.toISOString().split('T')[0],
        dayName: currentDate.toLocaleDateString('en-US', { weekday: 'long' }),
        location: trip.destination || 'Paris, France',
        activities: this.generateDayActivities(i + 1),
        accommodation: this.generateAccommodationInfo(i + 1),
        travel: this.generateTravelInfo(i + 1),
        meals: this.generateMealsInfo(i + 1),
        notes: this.generateDayNotes(i + 1),
        budget: `€${150 + (i * 25)}`
      });
    }

    return days;
  }

  generateDayActivities(dayNumber) {
    const activities = [
      ['Arrive at airport', 'Hotel check-in', 'Walk around neighborhood'],
      ['Museum visit', 'Lunch at local restaurant', 'City center exploration'],
      ['Day trip to nearby attraction', 'Local market visit', 'Cultural experience'],
      ['Shopping district', 'Park visit', 'Evening entertainment'],
      ['Final sightseeing', 'Souvenir shopping', 'Departure preparation']
    ];

    return activities[dayNumber - 1] || ['Free day', 'Relaxation', 'Personal time'];
  }

  generateAccommodationInfo(dayNumber) {
    if (dayNumber === 1) return 'Hotel check-in (Le Grand Hotel)';
    if (dayNumber === 5) return 'Hotel check-out';
    return 'Le Grand Hotel - Premium Suite';
  }

  generateTravelInfo(dayNumber) {
    if (dayNumber === 1) return 'Flight arrival - Air France AF123';
    if (dayNumber === 5) return 'Flight departure - Air France AF456';
    return 'Local transportation - Metro Day Pass';
  }

  generateMealsInfo(dayNumber) {
    const meals = [
      'Airport snack, Welcome dinner at hotel restaurant',
      'Breakfast included, Lunch at Café de la Paix, Dinner at Le Procope',
      'Breakfast included, Packed lunch for day trip, Dinner at local bistro',
      'Breakfast included, Lunch at food market, Fine dining experience',
      'Breakfast included, Light lunch before departure'
    ];

    return meals[dayNumber - 1] || 'Breakfast included, Lunch and dinner at leisure';
  }

  generateDayNotes(dayNumber) {
    const notes = [
      'Arrival day - take it easy, adjust to time zone',
      'Main sightseeing day - book museum tickets in advance',
      'Full day out - pack comfortable shoes and weather gear',
      'Flexible day - adjust plans based on weather and energy',
      'Departure day - check flight times and pack accordingly'
    ];

    return notes[dayNumber - 1] || 'Enjoy your day at your own pace';
  }

  /**
   * Generate Excel export
   */
  async generateExcelExport(itineraryData, tripInfo) {
    try {
      console.log('[EXPORT DEBUG] Generating Excel blob...');
      const blob = await excelGenerator.generateItineraryExcel(itineraryData, tripInfo);
      console.log('[EXPORT DEBUG] Blob created:', {
        type: blob.type,
        size: blob.size,
        isBlob: blob instanceof Blob
      });

      const filename = this.generateFilename(tripInfo, 'xlsx');
      console.log('[EXPORT DEBUG] Filename generated:', filename);

      excelGenerator.downloadExcelFile(blob, filename);
      console.log('[EXPORT DEBUG] Download initiated for Excel file');

    } catch (error) {
      console.error('[EXPORT ERROR] Excel export failed:', error);
      throw new Error('Failed to generate Excel file');
    }
  }

  /**
   * Generate CSV export
   */
  async generateCsvExport(itineraryData, tripInfo) {
    try {
      console.log('[EXPORT DEBUG] Generating CSV content...');
      const csvContent = await csvGenerator.generateItineraryCsv(itineraryData);
      console.log('[EXPORT DEBUG] CSV content generated:', {
        contentLength: csvContent?.length,
        contentPreview: csvContent?.substring(0, 200),
        isString: typeof csvContent === 'string'
      });

      const filename = this.generateFilename(tripInfo, 'csv');
      console.log('[EXPORT DEBUG] Filename generated:', filename);

      this.downloadFile(csvContent, filename, 'text/csv');
      console.log('[EXPORT DEBUG] Download initiated for CSV file');

    } catch (error) {
      console.error('[EXPORT ERROR] CSV export failed:', error);
      throw new Error('Failed to generate CSV file');
    }
  }

  /**
   * Helper methods
   */
  async getDaysCount(context) {
    // In real implementation, would call daysService
    if (context.trip?.startDate && context.trip?.endDate) {
      const start = new Date(context.trip.startDate);
      const end = new Date(context.trip.endDate);
      return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    }
    return 5; // Mock value
  }

  async getActivitiesCount(context) {
    // In real implementation, would count actual activities
    const days = await this.getDaysCount(context);
    return days * 3; // Assume 3 activities per day on average
  }

  calculateEstimatedSize(daysCount, activitiesCount) {
    const baseSize = 2; // 2KB base
    const sizePerDay = 0.5; // 0.5KB per day
    const totalKB = baseSize + (daysCount * sizePerDay);

    if (totalKB < 1) return '< 1KB';
    if (totalKB < 1024) return `${Math.ceil(totalKB)}KB`;
    return `${(totalKB / 1024).toFixed(1)}MB`;
  }

  formatDateRange(startDate, endDate) {
    if (!startDate || !endDate) return 'Date range not specified';

    const start = new Date(startDate);
    const end = new Date(endDate);
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    return `${startStr} - ${endStr}`;
  }

  generateFilename(tripInfo, format) {
    const tripName = (tripInfo?.name || 'trip').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    const date = new Date().toISOString().split('T')[0];
    return `${tripName}-itinerary-${date}`;
  }

  downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${mimeType.includes('csv') ? 'csv' : 'txt'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Notification methods (to be integrated with actual notification system)
   */
  showNotification(type, data) {
    const messages = {
      started: `Starting itinerary export in ${data?.toUpperCase()} format...`,
      completed: `Itinerary export completed successfully as ${data?.toUpperCase()}`,
      error: `Itinerary export error: ${data}`
    };

    console.log(messages[type]);

    // In a real implementation, this would integrate with:
    // - Material-UI Snackbar
    // - React Toast notifications
    // - Global notification service

    if (this.notifications[type]) {
      this.notifications[type](messages[type]);
    }
  }

  /**
   * Set notification handlers
   */
  setNotificationHandlers(handlers) {
    this.notifications = { ...this.notifications, ...handlers };
  }
}

export default new ItineraryExportService();